// --- Our Modules --------------------------------------------------------------------------------
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
import type { TyIconName } from "@/UI/atoms/Icon/types";
import ActionsPopup, { type TyAction } from "@/UI/popups/ActionsPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyAddOption = { key: string; label: string; icon: TyIconName; onSelect: () => void };

type Props = {
    label: string;
    options: TyAddOption[];
    /** The popup's name, so two add buttons on one screen cannot replace each other's menu. */
    name?: string;
    /**
     * The plus alone, square. It is what a bar with tabs asks for — the same rule `ActionsBar`
     * applies to its own add button, passed in here because this one rides the bar's `after` slot
     * and cannot see the tabs from inside it.
     */
    compact?: boolean;
};

/**
 * One add button, however many ways there are to add.
 *
 * Two primary buttons side by side make a person read both before pressing either, and the second is
 * always the rarer one. So there is one button: a single option fires straight away, and several open
 * a menu hanging off the button itself — under it, or above it when it sits near the foot of the
 * screen, which `PopupPaper` decides by measuring.
 *
 * On a phone it collapses to the plus alone, square, matching the filter button beside it. The words
 * are the first thing to go when the bar runs out of room, and the icon is unambiguous.
 */
export default function AddMenuButton({ label, options, name = "add-menu", compact }: Props) {
    const t = useTranslation();
    const addComp = usePopup((state) => state.addComp);

    const open = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (options.length === 1) return options[0].onSelect();

        const anchor = event.currentTarget.getBoundingClientRect();
        const actions: TyAction[] = options.map((option) => ({ key: option.key, label: option.label, icon: option.icon, onSelect: option.onSelect }));

        addComp({
            name,
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: { actions },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });
    };

    return (
        <button
            type="button"
            className={`add-menu${compact ? " add-menu--compact" : ""}`}
            onClick={open}
            aria-label={label}
            title={options.length === 1 ? label : t.common.more}
        >
            <Icon name="add" size={22} />
            {!compact && <span className="add-menu__label">{label}</span>}
        </button>
    );
}
